import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Button, ButtonGroup, Modal, Table } from 'react-bootstrap'

import cogoToast from 'cogo-toast'

import ProviderService from '../../../services/ProviderService'
import ConfirmationModal from '../../controls/ConfirmationModal'
import ProviderAutocomplete from '../../ProviderAutocomplete'
import ProviderForm from '../../ProviderForm'
import { detectProviderClassification } from '../HitlReview'

export const isMatchingProviderType = (p, sectionType) => {
  if (!sectionType) return true
  const sType = sectionType.trim().toLowerCase()
  const pType = (p?.type || '').trim().toLowerCase()

  if (sType.includes('treatment')) {
    return !pType.includes('pharmacy') && !pType.includes('equipment')
  }
  if (sType.includes('pharmacy')) {
    return pType.includes('pharmacy')
  }
  if (sType.includes('equipment')) {
    return pType.includes('equipment')
  }
  return pType.includes(sType)
}

export default function LinkedProvidersSection({ caseId, type, title, setModelValue, caseModel }) {
  const defaultClassification = type === 'Pharmacy' || type === 'Equipment' ? 'Organization' : 'Individual'
  const [providers, setProviders] = useState([])
  const [isAdding, setIsAdding] = useState(false)
  const [addingClassification, setAddingClassification] = useState(defaultClassification)
  const [createClassification, setCreateClassification] = useState(defaultClassification)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [clinicianToUnlink, setClinicianToUnlink] = useState(null)
  const [facilityToUnlink, setFacilityToUnlink] = useState(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [selectedEditProviderId, setSelectedEditProviderId] = useState(null)
  const [newFacilityPrefill, setNewFacilityPrefill] = useState(null)
  const [draggingClinician, setDraggingClinician] = useState(null)
  const [draggingSourceFacility, setDraggingSourceFacility] = useState(null)
  const [dragOverFacilityKey, setDragOverFacilityKey] = useState(null)
  const [quickLinkClinicianId, setQuickLinkClinicianId] = useState(null)
  const [isAffiliatingId, setIsAffiliatingId] = useState(null)
  const autocompleteRef = useRef(null)
  const sectionContainerRef = useRef(null)
  const quickLinkRowRef = useRef(null)
  const dragClientYRef = useRef(null)

  useEffect(() => {
    if (isAdding && autocompleteRef.current) {
      autocompleteRef.current.focus?.()
    }
  }, [isAdding])

  useEffect(() => {
    if (quickLinkClinicianId && quickLinkRowRef.current) {
      if (typeof quickLinkRowRef.current.scrollIntoView === 'function') {
        quickLinkRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [quickLinkClinicianId])

  useEffect(() => {
    if (!draggingClinician) {
      dragClientYRef.current = null
      return undefined
    }

    const scrollContainers = deltaY => {
      if (!deltaY || typeof window === 'undefined') return
      if (typeof window.scrollBy === 'function') {
        window.scrollBy(0, deltaY)
      }
      let el = sectionContainerRef.current?.parentElement
      while (el && el !== document.body && el !== document.documentElement) {
        if (el.scrollHeight > el.clientHeight && typeof window.getComputedStyle === 'function') {
          const style = window.getComputedStyle(el)
          if (/(auto|scroll)/.test(style.overflowY || style.overflow || '')) {
            el.scrollTop += deltaY
          }
        }
        el = el.parentElement
      }
    }

    const getVisibleBounds = () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return { top: 0, bottom: 800 }
      }
      const viewportHeight = window.innerHeight || 800
      let topOffset = 0
      const stickyTopSelectors = [
        '.case-header-sticky-wrapper',
        '.tab-bar-container',
        '.case-nav-bar',
        '.navbar',
        'header',
      ]
      stickyTopSelectors.forEach(sel => {
        const el = document.querySelector(sel)
        if (el && typeof el.getBoundingClientRect === 'function') {
          const rect = el.getBoundingClientRect()
          if (rect.bottom > 0 && rect.bottom < viewportHeight * 0.6) {
            topOffset = Math.max(topOffset, rect.bottom)
          }
        }
      })
      if (topOffset === 0) {
        const stickyWrapper = document.querySelector('.case-header-sticky-wrapper, .tab-bar-container, .case-nav-bar')
        const navbar = document.querySelector('.navbar')
        if (stickyWrapper || navbar) {
          topOffset = (navbar?.offsetHeight || 72) + (stickyWrapper?.offsetHeight || 115)
        }
      }
      const containerRect = sectionContainerRef.current?.getBoundingClientRect?.()
      if (containerRect && containerRect.top > topOffset && containerRect.top < viewportHeight * 0.55) {
        topOffset = containerRect.top
      }
      return { top: topOffset, bottom: viewportHeight }
    }

    const handleWindowDragOver = e => {
      if (typeof e.clientY === 'number' && e.clientY > 0) {
        dragClientYRef.current = e.clientY
      }
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'move'
      }
      e.preventDefault()
    }

    const handleWindowWheel = e => {
      if (typeof e.deltaY === 'number' && e.deltaY !== 0) {
        scrollContainers(e.deltaY)
      }
    }

    let rafId = null
    const TOP_EDGE_ZONE = 165
    const BOTTOM_EDGE_ZONE = 150
    const MAX_STEP = 20
    const tick = () => {
      const y = dragClientYRef.current
      if (typeof y === 'number' && typeof window !== 'undefined' && window.innerHeight > 0) {
        const { top: topBoundary, bottom: bottomBoundary } = getVisibleBounds()
        const topTriggerY = topBoundary + TOP_EDGE_ZONE
        const bottomTriggerY = bottomBoundary - BOTTOM_EDGE_ZONE
        if (y < topTriggerY) {
          const ratio = Math.min(1, Math.max(0.2, (topTriggerY - y) / TOP_EDGE_ZONE))
          scrollContainers(-Math.round(ratio * MAX_STEP))
        } else if (y > bottomTriggerY) {
          const ratio = Math.min(1, Math.max(0.2, (y - bottomTriggerY) / BOTTOM_EDGE_ZONE))
          scrollContainers(Math.round(ratio * MAX_STEP))
        }
      }
      rafId = window.requestAnimationFrame ? window.requestAnimationFrame(tick) : null
    }

    window.addEventListener('dragover', handleWindowDragOver, true)
    window.addEventListener('wheel', handleWindowWheel, { passive: true })
    if (typeof window.requestAnimationFrame === 'function') {
      rafId = window.requestAnimationFrame(tick)
    }

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver, true)
      window.removeEventListener('wheel', handleWindowWheel)
      if (rafId !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafId)
      }
    }
  }, [draggingClinician])

  useEffect(() => {
    if (!caseId) return
    ProviderService.getProvidersForCase(caseId)
      .then(allProviders => {
        setProviders(allProviders)

        if (setModelValue) {
          const t = (type || '').toLowerCase()
          const fieldKey = t.includes('treatment')
            ? 'treatmentProviders'
            : t.includes('equipment')
            ? 'equipmentProviders'
            : 'pharmacies'

          const filteredAll = allProviders.filter(p => isMatchingProviderType(p, type))

          const mapped = filteredAll.map(p => {
            const addr =
              p.addresses?.find(a => a.type === 'PrimaryPractice') ||
              p.addresses?.find(a => a.type === 'Mailing') ||
              p.addresses?.[0]
            const indivName = [p.firstName, p.lastName].filter(Boolean).join(' ') + (p.suffix ? `, ${p.suffix}` : '')
            const isOrg =
              p.classification === 'Organization' ||
              (!indivName.trim() && p.name && detectProviderClassification(p.name) === 'Organization')
            return {
              id: p.id,
              providerId: p.id,
              name:
                p.classification === 'Individual'
                  ? indivName.trim() || p.name || ''
                  : p.organizationName || p.name || '',
              companyName: p.parentProvider?.name || p.organizationName || (isOrg ? p.name : ''),
              organizationName: p.organizationName || (isOrg ? p.name : ''),
              classification: p.classification,
              parentProviderId: p.parentProviderId,
              affiliations: p.affiliations || [],
              address: addr
                ? {
                    line1: addr.line1,
                    city: addr.city,
                    state: addr.state,
                    zip: addr.zip,
                  }
                : null,
              phone: p.phone,
              fax: p.fax,
              email: p.email,
              speciality: p.specialties,
              specialty: p.specialties,
              npi: p.npi,
              isPrimaryCarePhysician: p.isPrimaryCarePhysician || false,
            }
          })
          const currentValue = caseModel ? caseModel[fieldKey] : null
          if (JSON.stringify(currentValue || []) !== JSON.stringify(mapped)) {
            setModelValue(fieldKey, mapped)
          }
        }
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.warn(err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, type, refreshTrigger])

  const handleLink = provider => {
    if (provider && provider.id) {
      ProviderService.linkToCase(caseId, provider.id).then(() => {
        setRefreshTrigger(prev => prev + 1)
        setIsAdding(false)
      })
    }
  }

  const handleUnlinkClinician = () => {
    if (clinicianToUnlink) {
      ProviderService.unlinkFromCase(caseId, clinicianToUnlink.id).then(() => {
        setRefreshTrigger(prev => prev + 1)
        setClinicianToUnlink(null)
      })
    }
  }

  const handleFacilityClick = async fac => {
    if (fac.id && fac.id !== '00000000-0000-0000-0000-000000000000') {
      try {
        const existing = await ProviderService.get(fac.id)
        if (existing) {
          setSelectedEditProviderId(fac.id)
          return
        }
      } catch {
        fac.id = null
      }
    }

    try {
      const results = await ProviderService.search({ searchText: fac.name, page: 1, take: 20 })
      const list = Array.isArray(results) ? results : results?.results || []
      const match = list.find(
        p =>
          (!p.isIndividual ||
            p.classification === 'Organization' ||
            detectProviderClassification(p.name || p.organization) === 'Organization') &&
          ((p.organization && p.organization.toLowerCase() === fac.name.toLowerCase()) ||
            (p.name && p.name.toLowerCase() === fac.name.toLowerCase()) ||
            (p.sortName && p.sortName.toLowerCase() === fac.name.toLowerCase()) ||
            (p.organizationName && p.organizationName.toLowerCase() === fac.name.toLowerCase())),
      )
      if (match && match.id && match.id !== '00000000-0000-0000-0000-000000000000') {
        try {
          const matchExisting = await ProviderService.get(match.id)
          if (matchExisting) {
            fac.id = match.id
            setSelectedEditProviderId(match.id)
            return
          }
        } catch {
          // match ID is not valid in DB
        }
      }
    } catch {
      // ignore search error
    }

    setNewFacilityPrefill({
      classification: 'Organization',
      name: fac.name,
      organizationName: fac.name,
      phone: fac.phone || '',
      addresses:
        fac.addresses && fac.addresses.length > 0
          ? fac.addresses
          : [
              { type: 'Mailing', line1: '', city: '', state: '', zip: '' },
              { type: 'PrimaryPractice', line1: '', city: '', state: '', zip: '' },
            ],
      individuals:
        fac.clinicians && fac.clinicians.length > 0
          ? fac.clinicians.map(({ clinician }) => ({
              id: clinician.id,
              firstName: clinician.firstName || '',
              lastName: clinician.lastName || '',
              title: clinician.suffix || clinician.specialties || '',
              email: clinician.email || '',
              phone: clinician.phone || '',
              npi: clinician.npi || '',
            }))
          : [{ firstName: '', lastName: '', title: '', email: '', phone: '', npi: '' }],
    })
    setShowCreateModal(true)
  }

  const handleUnlinkFacility = async (facility, unlinkCliniciansToo) => {
    try {
      if (facility.id && facility.id !== '00000000-0000-0000-0000-000000000000') {
        await ProviderService.unlinkFromCase(caseId, facility.id).catch(() => {})
      }
      if (unlinkCliniciansToo && facility.clinicians) {
        await Promise.all(
          facility.clinicians.map(({ clinician }) =>
            clinician && clinician.id ? ProviderService.unlinkFromCase(caseId, clinician.id) : Promise.resolve(),
          ),
        )
      } else if (!unlinkCliniciansToo && facility.clinicians) {
        // Disassociate clinicians from this facility so they remain on the case as independent practitioners
        await Promise.all(
          facility.clinicians.map(async ({ clinician }) => {
            if (!clinician || !clinician.id) return
            try {
              const current = await ProviderService.get(clinician.id)
              const updatedAffs = (current.affiliations || []).filter(
                a =>
                  (!facility.id || a.organizationId !== facility.id) &&
                  (!facility.name || a.organizationName?.toLowerCase() !== facility.name.toLowerCase()),
              )
              const updated = {
                ...current,
                affiliations: updatedAffs,
                parentProviderId: current.parentProviderId === facility.id ? null : current.parentProviderId,
                organizationName:
                  current.organizationName?.toLowerCase() === facility.name?.toLowerCase()
                    ? ''
                    : current.organizationName,
              }
              await ProviderService.update(clinician.id, updated)
            } catch {
              // ignore
            }
          }),
        )
      }
    } finally {
      setRefreshTrigger(prev => prev + 1)
      setFacilityToUnlink(null)
    }
  }

  const filteredProviders = providers.filter(p => isMatchingProviderType(p, type))

  const formatAddress = addresses => {
    if (!addresses || addresses.length === 0) return ''
    const addr =
      addresses.find(a => a.type === 'PrimaryPractice') || addresses.find(a => a.type === 'Mailing') || addresses[0]
    if (!addr) return ''
    const street = [addr.line1, addr.line2].filter(Boolean).join(' ')
    return [street, addr.city, [addr.state, addr.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  }

  const getIndivDisplayName = p => {
    const parts = [p.firstName, p.lastName].filter(Boolean).join(' ')
    const withSuffix = p.suffix ? `${parts}, ${p.suffix}` : parts
    return withSuffix.trim() || p.name || ''
  }

  const handleAffiliateClinicianWithFacility = async (clinician, targetFacility) => {
    if (!clinician || !clinician.id || !targetFacility) return
    const facName = (targetFacility.name || targetFacility.organizationName || targetFacility.organization || '').trim()
    const facId =
      targetFacility.id && targetFacility.id !== '00000000-0000-0000-0000-000000000000' ? targetFacility.id : null
    if (!facName && !facId) return

    setIsAffiliatingId(clinician.id)
    try {
      let current = clinician
      try {
        const fetched = await ProviderService.get(clinician.id)
        if (fetched) current = fetched
      } catch {
        // Fall back to in-memory clinician object
      }

      const existingAffs = Array.isArray(current.affiliations) ? current.affiliations : []
      const filteredAffs = existingAffs
        .filter(
          a =>
            (!facId || String(a.organizationId).toLowerCase() !== String(facId).toLowerCase()) &&
            (!facName || (a.organizationName || '').trim().toLowerCase() !== facName.toLowerCase()),
        )
        .map(a => ({ ...a, isPrimary: false }))

      const newPrimaryAff = {
        organizationId: facId,
        organizationName: facName,
        role: 'Primary Practice',
        isPrimary: true,
        address: targetFacility.addresses?.[0] || targetFacility.address || null,
        phone: targetFacility.phone || null,
      }

      const updatedPayload = {
        ...current,
        classification: 'Individual',
        organizationName: facName,
        parentProviderId: facId || current.parentProviderId || null,
        affiliations: [newPrimaryAff, ...filteredAffs],
      }

      await ProviderService.update(clinician.id, updatedPayload)
      if (caseId && facId) {
        await ProviderService.linkToCase(caseId, facId).catch(() => {})
      }

      setQuickLinkClinicianId(null)
      setDraggingClinician(null)
      setDraggingSourceFacility(null)
      setDragOverFacilityKey(null)
      setRefreshTrigger(prev => prev + 1)
      cogoToast.success(`Affiliated ${getIndivDisplayName(clinician)} with ${facName}`, { position: 'top-right' })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to affiliate clinician with facility:', err)
      cogoToast.error('Failed to update provider affiliation.', { position: 'top-right' })
    } finally {
      setIsAffiliatingId(null)
    }
  }

  const handleDisassociateClinicianFromFacility = async (clinician, sourceFacility = null) => {
    if (!clinician || !clinician.id) return
    const facId =
      sourceFacility?.id && sourceFacility.id !== '00000000-0000-0000-0000-000000000000' ? sourceFacility.id : null
    const facName = (sourceFacility?.name || sourceFacility?.organizationName || '').trim()

    setIsAffiliatingId(clinician.id)
    try {
      let current = clinician
      try {
        const fetched = await ProviderService.get(clinician.id)
        if (fetched) current = fetched
      } catch {
        // Fall back to in-memory clinician object
      }

      if (caseId && facId) {
        await ProviderService.linkToCase(caseId, facId).catch(() => {})
      }

      const existingAffs = Array.isArray(current.affiliations) ? current.affiliations : []
      const remainingAffs =
        facId || facName
          ? existingAffs.filter(
              a =>
                (!facId || String(a.organizationId).toLowerCase() !== String(facId).toLowerCase()) &&
                (!facName || (a.organizationName || '').trim().toLowerCase() !== facName.toLowerCase()),
            )
          : []

      const nextPrimary = remainingAffs.find(a => a.isPrimary) || remainingAffs[0] || null
      const normalizedAffs = remainingAffs.map(a => ({
        ...a,
        isPrimary: nextPrimary ? a === nextPrimary : false,
      }))

      const updatedPayload = {
        ...current,
        classification: current.classification === 'SoleProprietor' ? 'SoleProprietor' : 'Individual',
        organizationName: nextPrimary ? nextPrimary.organizationName || '' : '',
        parentProviderId: nextPrimary ? nextPrimary.organizationId || null : null,
        affiliations: normalizedAffs,
      }

      await ProviderService.update(clinician.id, updatedPayload)

      setDraggingClinician(null)
      setDraggingSourceFacility(null)
      setDragOverFacilityKey(null)
      setRefreshTrigger(prev => prev + 1)
      cogoToast.success(
        facName
          ? `Unlinked ${getIndivDisplayName(clinician)} from ${facName}`
          : `Moved ${getIndivDisplayName(clinician)} to Independent Practitioners`,
        { position: 'top-right' },
      )
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to unlink clinician from facility:', err)
      cogoToast.error('Failed to unlink clinician from facility.', { position: 'top-right' })
    } finally {
      setIsAffiliatingId(null)
    }
  }

  const handleAddClinicianToFacility = fac => {
    const facId = fac.id && fac.id !== '00000000-0000-0000-0000-000000000000' ? fac.id : null
    setNewFacilityPrefill({
      classification: 'Individual',
      organizationName: fac.name,
      parentProviderId: facId,
      phone: '',
      addresses: [
        { type: 'Mailing', line1: '', city: '', state: '', zip: '' },
        { type: 'PrimaryPractice', line1: '', city: '', state: '', zip: '' },
      ],
      affiliations: [
        {
          organizationId: facId,
          organizationName: fac.name,
          role: 'Primary Practice',
          isPrimary: true,
        },
      ],
    })
    setCreateClassification('Individual')
    setShowCreateModal(true)
  }

  // Build Option 1 Hierarchy: Facility Groups and Independent Clinicians
  const { facilityGroups, independentClinicians } = useMemo(() => {
    const facilitiesMap = new Map()
    const independents = []

    // 1. Pre-populate organization providers linked to case
    filteredProviders.forEach(p => {
      const indivName = [p.firstName, p.lastName].filter(Boolean).join(' ') + (p.suffix ? `, ${p.suffix}` : '')
      const isOrg =
        p.classification === 'Organization' ||
        (!indivName.trim() && p.name && detectProviderClassification(p.name) === 'Organization')
      if (isOrg) {
        const orgName = (p.organizationName || p.name || 'Unnamed Facility').trim()
        const facObj = {
          id: p.id && p.id !== '00000000-0000-0000-0000-000000000000' ? p.id : null,
          name: orgName,
          addresses: p.addresses || [],
          phone: p.phone,
          fax: p.fax,
          npi: p.npi,
          clinicians: [],
        }
        if (p.id) facilitiesMap.set(String(p.id).toLowerCase(), facObj)
        if (orgName) facilitiesMap.set(orgName.toLowerCase(), facObj)
      }
    })

    // 2. Associate individual providers with facilities via affiliations or parentProvider
    filteredProviders.forEach(p => {
      const indivName = [p.firstName, p.lastName].filter(Boolean).join(' ') + (p.suffix ? `, ${p.suffix}` : '')
      const isOrg =
        p.classification === 'Organization' ||
        (!indivName.trim() && p.name && detectProviderClassification(p.name) === 'Organization')
      if (isOrg) return

      if (p.classification === 'SoleProprietor') {
        independents.push(p)
        return
      }

      const affs = p.affiliations && p.affiliations.length > 0 ? p.affiliations : null
      if (affs) {
        let matchedAny = false
        affs.forEach(aff => {
          const affOrgName = (aff.organizationName || '').trim()
          const rawOrgId =
            aff.organizationId && aff.organizationId !== '00000000-0000-0000-0000-000000000000'
              ? String(aff.organizationId).toLowerCase()
              : null

          if (!affOrgName && !rawOrgId) return

          const nameKey = affOrgName ? affOrgName.toLowerCase() : null
          let fac = (rawOrgId && facilitiesMap.get(rawOrgId)) || (nameKey && facilitiesMap.get(nameKey))
          if (!fac) {
            for (const existingFac of facilitiesMap.values()) {
              if (
                (rawOrgId && existingFac.id && String(existingFac.id).toLowerCase() === rawOrgId) ||
                (affOrgName && existingFac.name.toLowerCase() === affOrgName.toLowerCase())
              ) {
                fac = existingFac
                break
              }
            }
          }

          if (!fac) {
            fac = {
              id: rawOrgId ? aff.organizationId : null,
              name: affOrgName || 'Associated Facility',
              addresses: aff.address ? [aff.address] : [],
              phone: aff.phone,
              fax: aff.fax,
              clinicians: [],
            }
            if (rawOrgId) facilitiesMap.set(rawOrgId, fac)
            if (nameKey) facilitiesMap.set(nameKey, fac)
          }

          if (fac) {
            if ((!fac.id || fac.id === '00000000-0000-0000-0000-000000000000') && rawOrgId) {
              fac.id = aff.organizationId
            }
            if ((!fac.addresses || fac.addresses.length === 0) && aff.address) {
              fac.addresses = [aff.address]
            }
            if (!fac.phone && aff.phone) {
              fac.phone = aff.phone
            }

            matchedAny = true
            fac.clinicians.push({
              clinician: p,
              role: aff.role || (aff.isPrimary ? 'Primary Practice' : 'Affiliated Clinician'),
              isPrimary: aff.isPrimary,
            })
          }
        })

        if (!matchedAny) {
          independents.push(p)
        }
      } else if (p.parentProviderId || p.organizationName || p.parentProvider) {
        const targetOrgName = (p.organizationName || p.parentProvider?.name || 'Associated Facility').trim()
        const rawParentId =
          p.parentProviderId && p.parentProviderId !== '00000000-0000-0000-0000-000000000000'
            ? String(p.parentProviderId).toLowerCase()
            : p.parentProvider?.id && p.parentProvider.id !== '00000000-0000-0000-0000-000000000000'
            ? String(p.parentProvider.id).toLowerCase()
            : null
        const nameKey = targetOrgName.toLowerCase()

        let fac = (rawParentId && facilitiesMap.get(rawParentId)) || facilitiesMap.get(nameKey)
        if (!fac) {
          for (const existingFac of facilitiesMap.values()) {
            if (
              (rawParentId && existingFac.id && String(existingFac.id).toLowerCase() === rawParentId) ||
              (targetOrgName && existingFac.name.toLowerCase() === targetOrgName.toLowerCase())
            ) {
              fac = existingFac
              break
            }
          }
        }

        if (!fac) {
          fac = {
            id: rawParentId ? p.parentProviderId || p.parentProvider?.id : null,
            name: targetOrgName,
            addresses: p.parentProvider?.addresses || [],
            phone: p.parentProvider?.phone,
            fax: p.parentProvider?.fax,
            clinicians: [],
          }
          if (rawParentId) facilitiesMap.set(rawParentId, fac)
          if (nameKey) facilitiesMap.set(nameKey, fac)
        }

        if ((!fac.id || fac.id === '00000000-0000-0000-0000-000000000000') && rawParentId) {
          fac.id = p.parentProviderId || p.parentProvider?.id
        }
        if ((!fac.addresses || fac.addresses.length === 0) && p.parentProvider?.addresses) {
          fac.addresses = p.parentProvider.addresses
        }
        if (!fac.phone && p.parentProvider?.phone) {
          fac.phone = p.parentProvider.phone
        }

        fac.clinicians.push({
          clinician: p,
          role: 'Primary Practice',
          isPrimary: true,
        })
      } else {
        independents.push(p)
      }
    })

    return {
      facilityGroups: Array.from(new Set(facilitiesMap.values())),
      independentClinicians: independents,
    }
  }, [filteredProviders])

  // Asynchronously resolve IDs for any facilities that don't have an ID yet
  useEffect(() => {
    const facilitiesWithoutId = facilityGroups.filter(f => !f.id || f.id === '00000000-0000-0000-0000-000000000000')
    if (facilitiesWithoutId.length === 0) return

    let isMounted = true
    Promise.all(
      facilitiesWithoutId.map(async fac => {
        try {
          const results = await ProviderService.search({ searchText: fac.name, page: 1, take: 20 })
          const list = Array.isArray(results) ? results : results?.results || []
          const match = list.find(
            p =>
              (!p.isIndividual ||
                p.classification === 'Organization' ||
                detectProviderClassification(p.name || p.organization) === 'Organization') &&
              ((p.organization && p.organization.toLowerCase() === fac.name.toLowerCase()) ||
                (p.name && p.name.toLowerCase() === fac.name.toLowerCase()) ||
                (p.sortName && p.sortName.toLowerCase() === fac.name.toLowerCase()) ||
                (p.organizationName && p.organizationName.toLowerCase() === fac.name.toLowerCase())),
          )
          if (match && match.id && match.id !== '00000000-0000-0000-0000-000000000000' && isMounted) {
            fac.id = match.id
            if (!fac.addresses || fac.addresses.length === 0)
              fac.addresses = match.addresses || (match.address ? [{ line1: match.address }] : [])
            if (!fac.phone) fac.phone = match.phone
            if (!fac.npi) fac.npi = match.npi
            setRefreshTrigger(prev => prev + 1)
          }
        } catch {
          // ignore background search error
        }
      }),
    )

    return () => {
      isMounted = false
    }
  }, [facilityGroups])

  const totalItemCount =
    facilityGroups.reduce((acc, f) => acc + (f.clinicians.length > 0 ? f.clinicians.length : 1), 0) +
    independentClinicians.length

  return (
    <div className="mb-4" ref={sectionContainerRef}>
      <div className="d-flex justify-content-between align-items-center pb-3">
        <h4 className="mb-0">{title}</h4>
        {!isAdding && (
          <Button variant="outline-primary" size="sm" onClick={() => setIsAdding(true)}>
            + Add Provider to This Case
          </Button>
        )}
      </div>

      {totalItemCount > 0 ? (
        <Table bordered hover responsive className="mb-3">
          <thead className="thead-light">
            <tr>
              <th style={{ width: '38%' }}>Provider / Facility</th>
              <th style={{ width: '24%' }}>Specialty / Role</th>
              <th style={{ width: '24%' }}>Contact & Address</th>
              <th style={{ width: '14%' }} className="text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Facility Groups */}
            {facilityGroups.map(fac => {
              const facKey = fac.id || fac.name
              const isDropTarget = dragOverFacilityKey === facKey
              return (
                <React.Fragment key={facKey}>
                  {/* Facility Header Row (Supports Drag-and-Drop Clinician Affiliation) */}
                  <tr
                    className={`facility-header-row font-weight-bold ${
                      isDropTarget ? 'table-primary' : 'table-secondary'
                    }`}
                    style={
                      isDropTarget
                        ? { outline: '2px dashed #007bff', transition: 'background-color 0.15s ease' }
                        : undefined
                    }
                    onDragOver={e => {
                      if (!draggingClinician) return
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                      if (dragOverFacilityKey !== facKey) {
                        setDragOverFacilityKey(facKey)
                      }
                    }}
                    onDragLeave={() => {
                      if (dragOverFacilityKey === facKey) {
                        setDragOverFacilityKey(null)
                      }
                    }}
                    onDrop={e => {
                      e.preventDefault()
                      setDragOverFacilityKey(null)
                      if (draggingClinician) {
                        handleAffiliateClinicianWithFacility(draggingClinician, fac)
                      }
                    }}
                  >
                    <td colSpan={2}>
                      <div className="d-flex align-items-center flex-wrap">
                        <span className="mr-2" role="img" aria-label="facility" style={{ fontSize: '1.2rem' }}>
                          🏢
                        </span>
                        <div>
                          <Button
                            variant="link"
                            className="p-0 text-left font-weight-bold text-dark align-baseline"
                            onClick={() => handleFacilityClick(fac)}
                          >
                            {fac.name}
                          </Button>
                          <Badge variant="secondary" className="ml-2 font-weight-normal">
                            Facility
                          </Badge>
                          {fac.npi && <span className="text-muted ml-2 small font-weight-normal">NPI: {fac.npi}</span>}
                          {isDropTarget && draggingClinician && (
                            <Badge variant="primary" className="ml-2 py-1 px-2">
                              Drop to affiliate {getIndivDisplayName(draggingClinician)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="small text-muted">
                        {formatAddress(fac.addresses) && <div>{formatAddress(fac.addresses)}</div>}
                        {fac.phone && <div>📞 {fac.phone}</div>}
                      </div>
                    </td>
                    <td className="text-right">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mr-1 py-0 px-2 add-facility-clinician-btn"
                        title={`Add or affiliate a clinician with ${fac.name}`}
                        onClick={() => handleAddClinicianToFacility(fac)}
                      >
                        + Clinician
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mr-1 py-0 px-2"
                        onClick={() => handleFacilityClick(fac)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="py-0 px-2"
                        onClick={() => setFacilityToUnlink(fac)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>

                  {/* Indented Clinician Rows */}
                  {fac.clinicians.length > 0 ? (
                    fac.clinicians.map(({ clinician, role, isPrimary }) => {
                      const indivName = getIndivDisplayName(clinician)
                      const isBusy = isAffiliatingId === clinician.id
                      return (
                        <tr
                          key={`${facKey}_${clinician.id}`}
                          className="clinician-row"
                          draggable={true}
                          onDragStart={e => {
                            if (typeof e.clientY === 'number' && e.clientY > 0) {
                              dragClientYRef.current = e.clientY
                            }
                            setDraggingClinician(clinician)
                            setDraggingSourceFacility(fac)
                            if (e.dataTransfer) {
                              e.dataTransfer.effectAllowed = 'move'
                              e.dataTransfer.setData('text/plain', String(clinician.id))
                            }
                          }}
                          onDragEnd={() => {
                            setDraggingClinician(null)
                            setDraggingSourceFacility(null)
                            setDragOverFacilityKey(null)
                          }}
                        >
                          <td style={{ paddingLeft: '2.5rem' }}>
                            <div className="d-flex align-items-center">
                              <span
                                className="text-muted mr-2 drag-handle"
                                title="Drag onto another facility to re-affiliate, or onto Independent Practitioners to unlink"
                                style={{ cursor: 'grab', userSelect: 'none' }}
                              >
                                ⠿ ↳
                              </span>
                              <span className="mr-2" role="img" aria-label="clinician">
                                👤
                              </span>
                              <div>
                                <Button
                                  variant="link"
                                  className="p-0 text-left font-weight-bold text-primary align-baseline"
                                  onClick={() => setSelectedEditProviderId(clinician.id)}
                                >
                                  {indivName}
                                </Button>
                                {clinician.npi && <div className="text-muted small">NPI: {clinician.npi}</div>}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>{clinician.specialties || clinician.type || 'Treatment Provider'}</div>
                            {role && (
                              <Badge variant={isPrimary ? 'primary' : 'light'} className="mt-1">
                                {role}
                              </Badge>
                            )}
                          </td>
                          <td>
                            <div className="small text-muted">
                              {clinician.phone && <div>📞 {clinician.phone}</div>}
                              {clinician.email && <div>✉️ {clinician.email}</div>}
                              {!clinician.phone && !clinician.email && (
                                <span className="font-italic">Same as facility</span>
                              )}
                            </div>
                          </td>
                          <td className="text-right">
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="mr-1 py-0 px-2 unlink-facility-clinician-btn"
                              disabled={isBusy}
                              title={`Unlink ${indivName} from ${fac.name} (keep on case as an Independent Practitioner)`}
                              onClick={() => handleDisassociateClinicianFromFacility(clinician, fac)}
                            >
                              {isBusy ? 'Unlinking…' : 'Unlink'}
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="mr-1 py-0 px-2"
                              onClick={() => setSelectedEditProviderId(clinician.id)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="py-0 px-2"
                              title="Remove provider from this case"
                              onClick={() => setClinicianToUnlink(clinician)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} style={{ paddingLeft: '2.5rem' }} className="text-muted small font-italic">
                        ↳ No individual clinicians assigned to this facility on this case. Drag an independent
                        practitioner here or click &ldquo;+ Clinician&rdquo;.
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}

            {/* Independent Practitioners Group */}
            {(independentClinicians.length > 0 || Boolean(draggingClinician && draggingSourceFacility)) && (
              <React.Fragment>
                <tr
                  className={`independent-header-row font-weight-bold ${
                    dragOverFacilityKey === '__independent__' ? 'table-primary' : 'table-secondary'
                  }`}
                  style={
                    dragOverFacilityKey === '__independent__'
                      ? { outline: '2px dashed #007bff', transition: 'background-color 0.15s ease' }
                      : undefined
                  }
                  onDragOver={e => {
                    if (!draggingClinician || !draggingSourceFacility) return
                    e.preventDefault()
                    e.dataTransfer.dropEffect = 'move'
                    if (dragOverFacilityKey !== '__independent__') {
                      setDragOverFacilityKey('__independent__')
                    }
                  }}
                  onDragLeave={() => {
                    if (dragOverFacilityKey === '__independent__') {
                      setDragOverFacilityKey(null)
                    }
                  }}
                  onDrop={e => {
                    e.preventDefault()
                    setDragOverFacilityKey(null)
                    if (draggingClinician && draggingSourceFacility) {
                      handleDisassociateClinicianFromFacility(draggingClinician, draggingSourceFacility)
                    }
                  }}
                >
                  <td colSpan={2}>
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="mr-2" role="img" aria-label="independent" style={{ fontSize: '1.2rem' }}>
                        👤
                      </span>
                      <span className="text-dark">Independent Practitioners</span>
                      <Badge variant="info" className="ml-2 font-weight-normal">
                        Independent
                      </Badge>
                      {dragOverFacilityKey === '__independent__' && draggingClinician && draggingSourceFacility ? (
                        <Badge variant="primary" className="ml-2 py-1 px-2">
                          Drop to unlink {getIndivDisplayName(draggingClinician)} from facility
                        </Badge>
                      ) : (
                        facilityGroups.length > 0 && (
                          <span className="text-muted small font-weight-normal ml-2">
                            (Drag onto a facility above to link, or drag an affiliated clinician here to unlink)
                          </span>
                        )
                      )}
                    </div>
                  </td>
                  <td colSpan={2}></td>
                </tr>
                {independentClinicians.length === 0 && Boolean(draggingClinician && draggingSourceFacility) && (
                  <tr
                    className={dragOverFacilityKey === '__independent__' ? 'table-primary' : ''}
                    onDragOver={e => {
                      if (!draggingClinician || !draggingSourceFacility) return
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'move'
                      if (dragOverFacilityKey !== '__independent__') {
                        setDragOverFacilityKey('__independent__')
                      }
                    }}
                    onDrop={e => {
                      e.preventDefault()
                      setDragOverFacilityKey(null)
                      if (draggingClinician && draggingSourceFacility) {
                        handleDisassociateClinicianFromFacility(draggingClinician, draggingSourceFacility)
                      }
                    }}
                  >
                    <td colSpan={4} style={{ paddingLeft: '2.5rem' }} className="text-muted small font-italic">
                      ↳ Drop {getIndivDisplayName(draggingClinician)} here to unlink from{' '}
                      {draggingSourceFacility?.name || 'their facility'} and keep on case as an Independent
                      Practitioner.
                    </td>
                  </tr>
                )}
                {independentClinicians.map(clinician => {
                  const indivName = getIndivDisplayName(clinician)
                  const isQuickLinkOpen = quickLinkClinicianId === clinician.id
                  const isBusy = isAffiliatingId === clinician.id
                  return (
                    <React.Fragment key={`indep_${clinician.id}`}>
                      <tr
                        className="clinician-row independent-clinician-row"
                        draggable={true}
                        onDragStart={e => {
                          if (typeof e.clientY === 'number' && e.clientY > 0) {
                            dragClientYRef.current = e.clientY
                          }
                          setDraggingClinician(clinician)
                          setDraggingSourceFacility(null)
                          if (e.dataTransfer) {
                            e.dataTransfer.effectAllowed = 'move'
                            e.dataTransfer.setData('text/plain', String(clinician.id))
                          }
                        }}
                        onDragEnd={() => {
                          setDraggingClinician(null)
                          setDraggingSourceFacility(null)
                          setDragOverFacilityKey(null)
                        }}
                        onDragOver={e => {
                          if (!draggingClinician || !draggingSourceFacility) return
                          e.preventDefault()
                          e.dataTransfer.dropEffect = 'move'
                          if (dragOverFacilityKey !== '__independent__') {
                            setDragOverFacilityKey('__independent__')
                          }
                        }}
                        onDrop={e => {
                          if (!draggingClinician || !draggingSourceFacility) return
                          e.preventDefault()
                          setDragOverFacilityKey(null)
                          handleDisassociateClinicianFromFacility(draggingClinician, draggingSourceFacility)
                        }}
                      >
                        <td style={{ paddingLeft: '2.5rem' }}>
                          <div className="d-flex align-items-center">
                            <span
                              className="text-muted mr-2 drag-handle"
                              title="Drag onto a facility row above to affiliate"
                              style={{ cursor: 'grab', userSelect: 'none' }}
                            >
                              ⠿ ↳
                            </span>
                            <span className="mr-2" role="img" aria-label="clinician">
                              👤
                            </span>
                            <div>
                              <Button
                                variant="link"
                                className="p-0 text-left font-weight-bold text-primary align-baseline"
                                onClick={() => setSelectedEditProviderId(clinician.id)}
                              >
                                {indivName}
                              </Button>
                              {clinician.npi && <div className="text-muted small">NPI: {clinician.npi}</div>}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>{clinician.specialties || clinician.type || 'Treatment Provider'}</div>
                          {clinician.classification === 'SoleProprietor' && (
                            <Badge variant="secondary" className="mt-1">
                              Sole Proprietor
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="small text-muted">
                            {formatAddress(clinician.addresses) && <div>{formatAddress(clinician.addresses)}</div>}
                            {clinician.phone && <div>📞 {clinician.phone}</div>}
                            {clinician.email && <div>✉️ {clinician.email}</div>}
                          </div>
                        </td>
                        <td className="text-right">
                          <Button
                            variant={isQuickLinkOpen ? 'primary' : 'outline-primary'}
                            size="sm"
                            className="mr-1 py-0 px-2 quick-link-facility-btn"
                            disabled={isBusy}
                            title="Link this practitioner to a facility or clinic"
                            onClick={() =>
                              setQuickLinkClinicianId(prev => (prev === clinician.id ? null : clinician.id))
                            }
                          >
                            {isBusy ? 'Linking…' : 'Link to Facility'}
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="mr-1 py-0 px-2"
                            onClick={() => setSelectedEditProviderId(clinician.id)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="py-0 px-2"
                            title="Remove provider from this case"
                            onClick={() => setClinicianToUnlink(clinician)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                      {isQuickLinkOpen && (
                        <tr ref={quickLinkRowRef} className="quick-link-facility-row bg-light">
                          <td colSpan={4} style={{ paddingLeft: '2.5rem' }}>
                            <div className="p-2 border rounded bg-white">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="small font-weight-bold text-dark">
                                  Affiliate <strong>{indivName}</strong> with an Organization / Facility:
                                </span>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-secondary"
                                  onClick={() => setQuickLinkClinicianId(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                              {facilityGroups.length > 0 && (
                                <div className="mb-2 d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
                                  <span className="small text-muted mr-1">Facilities on this case:</span>
                                  {facilityGroups.map(fac => (
                                    <Button
                                      key={`quicklink_${fac.id || fac.name}`}
                                      variant="outline-primary"
                                      size="sm"
                                      className="py-0 px-2 quick-link-case-facility-pill"
                                      disabled={isBusy}
                                      onClick={() => handleAffiliateClinicianWithFacility(clinician, fac)}
                                    >
                                      🏢 {fac.name}
                                    </Button>
                                  ))}
                                </div>
                              )}
                              <div style={{ maxWidth: '520px' }}>
                                <ProviderAutocomplete
                                  onChange={() => {}}
                                  onProviderSelect={selectedOrg => {
                                    if (selectedOrg) {
                                      handleAffiliateClinicianWithFacility(clinician, selectedOrg)
                                    }
                                  }}
                                  type={type}
                                  filter={{ classification: 'Organization' }}
                                  placeholder="Or search directory for an Organization by name or NPI..."
                                  value=""
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </React.Fragment>
            )}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted font-italic">No providers linked to this case.</p>
      )}

      {isAdding && (
        <div className="card p-3 bg-light mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
            <h6 className="mb-0">
              {addingClassification === 'Organization'
                ? 'Search for an Organization to Add to Case'
                : 'Search for an Individual Provider to Add to Case'}
            </h6>
            <div className="d-flex align-items-center mt-2 mt-sm-0">
              <span className="text-muted small font-weight-bold mr-2">Provider Type:</span>
              <ButtonGroup size="sm" aria-label="Filter by Individual or Organization">
                <Button
                  type="button"
                  variant={addingClassification === 'Individual' ? 'primary' : 'outline-secondary'}
                  onClick={() => setAddingClassification('Individual')}
                >
                  <span role="img" aria-label="individual" className="mr-1">
                    👤
                  </span>
                  Individual
                </Button>
                <Button
                  type="button"
                  variant={addingClassification === 'Organization' ? 'primary' : 'outline-secondary'}
                  onClick={() => setAddingClassification('Organization')}
                >
                  <span role="img" aria-label="organization" className="mr-1">
                    🏢
                  </span>
                  Organization
                </Button>
              </ButtonGroup>
            </div>
          </div>
          <ProviderAutocomplete
            ref={autocompleteRef}
            onChange={() => {}}
            onProviderSelect={handleLink}
            type={type}
            filter={{ classification: addingClassification }}
            placeholder={
              addingClassification === 'Organization'
                ? 'Search for an Organization by name or NPI...'
                : 'Search for an Individual Provider by name or NPI...'
            }
            value=""
          />
          <div className="mt-2 d-flex justify-content-end align-items-center flex-wrap">
            <span className="mr-2 text-muted small">Not in Library?</span>
            <Button
              variant="outline-primary"
              size="sm"
              className="mr-2"
              onClick={() => {
                setNewFacilityPrefill(null)
                setCreateClassification('Individual')
                setShowCreateModal(true)
              }}
            >
              <span role="img" aria-label="individual" className="mr-1">
                👤
              </span>
              + Create New Individual
            </Button>
            <Button
              variant="outline-primary"
              size="sm"
              className="mr-2"
              onClick={() => {
                setNewFacilityPrefill(null)
                setCreateClassification('Organization')
                setShowCreateModal(true)
              }}
            >
              <span role="img" aria-label="organization" className="mr-1">
                🏢
              </span>
              + Create New Organization
            </Button>
            <Button variant="link" size="sm" className="text-secondary" onClick={() => setIsAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      <ProviderForm
        show={showCreateModal}
        onHide={() => {
          setShowCreateModal(false)
          setNewFacilityPrefill(null)
        }}
        initialData={newFacilityPrefill}
        initialClassification={newFacilityPrefill?.classification || createClassification}
        onSuccess={newProvider => {
          if (newProvider && newProvider.id) {
            handleLink(newProvider)
          }
          setShowCreateModal(false)
          setNewFacilityPrefill(null)
        }}
        initialType={type}
      />

      <ProviderForm
        show={!!selectedEditProviderId}
        onHide={() => setSelectedEditProviderId(null)}
        providerId={selectedEditProviderId}
        onSuccess={() => {
          setRefreshTrigger(prev => prev + 1)
          setSelectedEditProviderId(null)
        }}
        initialType={type}
      />

      {clinicianToUnlink && (
        <ConfirmationModal
          title="Remove Provider from Case"
          body={`Are you sure you want to remove ${getIndivDisplayName(clinicianToUnlink)} from this case?`}
          onConfirm={handleUnlinkClinician}
          onClose={() => setClinicianToUnlink(null)}
          confirmText="Remove"
          cancelText="Cancel"
        />
      )}

      {facilityToUnlink && (
        <Modal show={true} onHide={() => setFacilityToUnlink(null)}>
          <Modal.Header closeButton>
            <Modal.Title>Remove Facility from Case</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Are you sure you want to remove <strong>{facilityToUnlink.name}</strong> from this case?
            </p>
            {facilityToUnlink.clinicians.length > 0 && (
              <div className="alert alert-warning py-2 mb-2">
                <div className="font-weight-bold mb-1">
                  Associated Clinician(s) on this case ({facilityToUnlink.clinicians.length}):
                </div>
                <ul className="mb-0 pl-3">
                  {facilityToUnlink.clinicians.map(({ clinician }) => (
                    <li key={clinician.id}>{getIndivDisplayName(clinician)}</li>
                  ))}
                </ul>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-between">
            <Button variant="secondary" onClick={() => setFacilityToUnlink(null)}>
              Cancel
            </Button>
            <div>
              {facilityToUnlink.clinicians.length > 0 && (
                <Button
                  variant="outline-danger"
                  className="mr-2"
                  onClick={() => handleUnlinkFacility(facilityToUnlink, false)}
                >
                  Remove Facility Only
                </Button>
              )}
              <Button variant="danger" onClick={() => handleUnlinkFacility(facilityToUnlink, true)}>
                {facilityToUnlink.clinicians.length > 0 ? 'Remove Facility & Clinicians' : 'Remove Facility'}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  )
}
